import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { PropsWithChildren } from 'react';

import { useIsDarkMode } from '@components/dark-mode';
import { ToastContainer } from '@components/toast/toast';

import ModalDimmer from './modal-dimmer';
import useGlobalStyles from './use-global-styles';
import s from './layout.scss';

export interface LayoutWrapperProps {
  withDoodleBackground?: boolean;
  error?: boolean;
  staticError?: boolean;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  isSwitched?: boolean;
  skeleton?: boolean;
}

/**
 * Common layout wrapper for the application. No business logic. Carries global
 * styles and offers a few layout variations for different states.
 */
export const LayoutWrapper = ({
  children,
  withDoodleBackground = false,
  error = false,
  staticError = false,
  isAuthenticated = false,
  isAdmin = false,
  isSwitched = false,
  skeleton = false,
}: PropsWithChildren<LayoutWrapperProps>) => {
  useGlobalStyles();
  useStyles(s);

  const isDarkMode = useIsDarkMode();

  return (
    <>
      <div
        id="layout"
        className={classNames(s.root, {
          dark: isDarkMode,
          [s.default]: !isAuthenticated,
          [s.skeleton]: skeleton,
          [s.staticError]: staticError,
          [s.admin]: isAuthenticated && isAdmin,
          [s.standard]: isAuthenticated && !isAdmin,
          [s.switched]: isAuthenticated && isSwitched,
        })}
      >
        {!skeleton && !isAuthenticated && (
          <div
            id="bgColor"
            className={classNames(s.bg, {
              [s.error]: error,
            })}
          />
        )}
        {withDoodleBackground && <div id="bgImage" className={s.doodle} />}
        {children}
        <ToastContainer
          position="bottom-right"
          theme={isDarkMode ? 'dark' : 'light'}
          closeOnClick
          draggable
        />
      </div>

      <ModalDimmer />
    </>
  );
};

export default LayoutWrapper;
